import { Alert, Divider, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  CalculateByField,
  ConditionEqualityType,
  DimensionDescriptor,
  MeasureUnitTable,
} from "../../../../../../shared/reporting/api/biClient.types";
import useDeferredDictionaryLoading from "../../../../../../shared/reporting/components/viewer/common/hooks/useDeferredDictionaryLoading";
import { useBiCommonDataContext } from "../../../../../../shared/reporting/contexts/BiCommonDataContext.ts";
import { formatDimension } from "../../../../../../shared/reporting/formatters/NumberFormatter.ts";
import cloneDeep from "../../../../../../shared/utilities/cloneDeep";
import { generateGuid } from "../../../../../../shared/utilities/generateGuid";
import {
  CellDrillDownInfo,
  CellDrillDownInfoType,
  DrillDownRow,
  MeasureDrillDownInfo,
} from "../../../../../api/biApi.types";
import { FieldWithOrder } from "../../../../../hooks/FieldWithOrder";
import useDrillDown from "../../../../../hooks/useDrillDown";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import { selectDimensions, selectDimensionsStructure } from "../../../../../store/selectors/metaDataSelectors";
import { selectDrillDownDefaultDimensions } from "../../../../../store/slices/drillDownConfigurationSlice";
import { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import { createDrillDownPageAction } from "../../../../../store/utils/storeUtils";
import CollapseContainer from "../../../../common/CollapseContainer";
import useCommonReportHandlers from "../../hooks/useCommonReportHandlers";
import ColumnsOptions from "../columns/ColumnsOptions";
import ConditionsCollapsedHeader from "../ConditionsCollapsedHeader";
import ConditionsPanel from "../ConditionsPanel";
import DrillDownGridWrapper from "../DrillDownGridWrapper";
import RecordsCounter from "../RecordsCounter";
import ToolBar from "../ToolBar";

interface Props {
  configuration: DrillDownPage;
  info: MeasureDrillDownInfo;
}

export default function LedgerPart({ configuration, info }: Props) {
  const dimensions = useSelector(selectDimensions);
  const dimensionsStructure = useSelector(selectDimensionsStructure);
  const defaultDimensions = useSelector(selectDrillDownDefaultDimensions);

  const state = useDrillDown(info);
  const dispatch = useAppDispatch();
  const { dataSetId } = useBiCommonDataContext();
  const [showColumnsOptions, setShowColumnsOptions] = useState<boolean | undefined>();
  const filteredDimensions = useMemo(
    () => (info.table === MeasureUnitTable.Gl ? dimensions : dimensions.filter((d) => !glTableFields.includes(d.name))),
    [dimensions, info.table]
  );
  const handlers = useCommonReportHandlers();

  const withAllocation = useMemo(() => info.allocated === true, [info.allocated]);
  useDeferredDictionaryLoading(
    dataSetId,
    { values: state.conditions, updateItem: state.actions.updateCondition },
    handlers.onLoadDictionaries
  );

  useEffect(() => {
    const fields = getDefaultFields(defaultDimensions, withAllocation, filteredDimensions);
    state.actions.changeSelectedFields(fields);
    const entryNoField = fields.find((f) => f.field.name === "EntryNo");
    if (entryNoField) {
      state.actions.changeFieldSorting([{ field: entryNoField.field, sortAsc: true, order: 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDimensions, withAllocation]);

  const handleSubDrillDown = useCallback(
    (row: DrillDownRow) => {
      const newConfiguration = createSubDrillDown(row, configuration, info, filteredDimensions);
      if (newConfiguration !== undefined) dispatch(createDrillDownPageAction("add", newConfiguration));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredDimensions, configuration, state.conditions]
  );

  const hideColumnsOptions = useCallback(() => setShowColumnsOptions(false), []);

  return (
    <Grid container sx={{ position: "relative", display: "flex", flex: 1, flexDirection: "row", width: "100%" }}>
      <CollapseContainer collapseElement={<ConditionsCollapsedHeader conditionsCount={state.conditions.length} />}>
        <ConditionsPanel state={state} configuration={configuration} info={info} />
      </CollapseContainer>
      <Grid container sx={{ flex: 1, width: "unset", flexDirection: "column" }}>
        <Grid container sx={{ flex: 1, width: "unset", flexDirection: "column", pl: "1.3rem", py: 2, pr: 3, gap: 2 }}>
          <ToolBar state={state} onShowColumnsOptions={() => setShowColumnsOptions(true)} />
          {state.error && (
            <Alert sx={{ mb: ".5rem" }} severity="error">
              {state.error}
            </Alert>
          )}
          {configuration && info.conditions && info.conditions.length > 0 && (
            <DrillDownGridWrapper state={state} onDrillDown={handleSubDrillDown} info={info} />
          )}
        </Grid>
        <Divider />
        <Grid container sx={{ backgroundColor: grey[50], width: "100%" }}>
          <RecordsCounter loading={false} skip={state.skip} take={state.take} totalCount={state.rowsTotal} />
        </Grid>
      </Grid>
      {showColumnsOptions !== undefined && (
        <ColumnsOptions
          dimensions={filteredDimensions}
          selectedFields={state.selectedFields}
          sortedFields={state.sortedFields}
          groupStructure={dimensionsStructure}
          open={showColumnsOptions === true}
          withAllocation={withAllocation}
          onApplySelection={state.actions.changeSelectedFields}
          onApplySorting={state.actions.changeFieldSorting}
          onClose={hideColumnsOptions}
        />
      )}
    </Grid>
  );
}

const glTableFields = ["CreditLcy", "CreditPcy", "DebitLcy", "DebitPcy"];

function getDefaultFields(defaultDimensions: string[], withAllocation: boolean, allFields: DimensionDescriptor[]) {
  const onlyAllocatedDimensions = ["InvestorTypeCode", "InvestorName", "Shares", "TransactionNo"];

  return defaultDimensions
    .filter((f) => (withAllocation ? true : !onlyAllocatedDimensions.includes(f)))
    .map<FieldWithOrder | undefined>((name, index) => {
      const field = allFields.find((f) => f.name === name);
      if (field) {
        return { field, order: index, canBeRemoved: name !== "EntryNo" };
      }
      return undefined;
    })
    .filter((item): item is FieldWithOrder => !!item);
}

function createSubDrillDown(
  row: DrillDownRow,
  configuration: DrillDownPage,
  info: MeasureDrillDownInfo,
  dimensions: DimensionDescriptor[]
) {
  const ledgerEntryNoDimension = dimensions.find((d) => d.name === "LedgerEntryNo");
  const entryNo = row["EntryNo"];

  if (entryNo === undefined || ledgerEntryNoDimension === undefined) return undefined;

  const value = getSubDrillDownFormattedValue(info, row, dimensions);
  const measure = getSubDrillDownMeasure(configuration, row);

  const newConfiguration: DrillDownPage = {
    id: generateGuid(),
    measure,
    cell: {
      value: undefined,
      formattedValue: value || "",
    },
    info: {
      combined: {
        drillDownInfoType: CellDrillDownInfoType.Default,
        field: info.field || CalculateByField.Lcy,
        table: info.table || MeasureUnitTable.Gl,
        allocated: true,
        conditions: [
          {
            dimensionName: ledgerEntryNoDimension.name,
            equalityType: ConditionEqualityType.Equal,
            values: [entryNo],
          },
        ],
        amountType: info.amountType,
      } as CellDrillDownInfo,
      items: [
        {
          drillDownInfoType: CellDrillDownInfoType.Default,
          field: info.field || CalculateByField.Lcy,
          table: info.table || MeasureUnitTable.Gl,
          allocated: true,
          conditions: [
            {
              dimensionName: ledgerEntryNoDimension.name,
              equalityType: ConditionEqualityType.Equal,
              values: [entryNo],
            },
          ],
          amountType: info.amountType,
        } as CellDrillDownInfo,
      ],
    },
    onlyLedger: true,
    chartOfAccounts: false,
  };

  return newConfiguration;
}

function getSubDrillDownFormattedValue(
  info: MeasureDrillDownInfo,
  row: DrillDownRow,
  dimensions: DimensionDescriptor[]
) {
  const valueFieldMapping = {
    [CalculateByField.Lcy]: "AmountLcy",
    [CalculateByField.Pcy]: "AmountPcy",
    [CalculateByField.Shares]: "Shares",
    [CalculateByField.AdditionalLcy]: "AdditionalAmountLcy",
  };
  const valueField = valueFieldMapping[info?.field || CalculateByField.Lcy];
  let value = row[valueField];
  const valueDimension = dimensions.find((d) => d.name === valueField);
  if (valueDimension !== undefined) {
    value = formatDimension(valueDimension, value);
  }
  return value;
}
function getSubDrillDownMeasure(configuration: DrillDownPage, row: Record<string, string>) {
  const measure = cloneDeep(configuration.measure);
  if (measure !== undefined) {
    measure.config.customLabel = getSubDrillDownMeasureCaption(row);
  }

  return measure;
}
function getSubDrillDownMeasureCaption(row: Record<string, string>) {
  const accountNo = row["AccountNo"];
  const accountName = row["AccountName"];
  if (accountNo && accountName) {
    return `${accountNo} - ${accountName}`;
  }

  return accountNo || accountName || "";
}
